'use client';

import { useState, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { LOCALES, type Locale } from '@/lib/i18n/i18n-config';
import type { Route } from 'next';
import styles from './LanguageSwitcher.module.scss';

interface LanguageSwitcherProps {
  currentLocale: Locale;
  className?: string;
}

export function LanguageSwitcher({
  currentLocale,
  className = '',
}: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const switchLocale = (newLocale: Locale) => {
    // Get current hash if present
    const hash = window.location.hash;

    // Replace the locale segment in the pathname
    const segments = pathname.split('/');
    segments[1] = newLocale;
    const newPath = segments.join('/');

    // Navigate to new locale path with preserved hash
    router.push((newPath + hash) as Route);

    // Close dropdown after selection
    setIsOpen(false);
  };

  // Filter out current locale to show only other options in dropdown
  const otherLocales = LOCALES.filter((locale) => locale !== currentLocale);

  return (
    <div className={`${styles.switcher} ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={styles.currentButton}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`Change language. Current language: ${currentLocale.toUpperCase()}`}
        type="button"
      >
        {currentLocale.toUpperCase()}
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          {otherLocales.map((locale) => (
            <button
              key={locale}
              onClick={() => switchLocale(locale)}
              className={styles.dropdownItem}
              aria-label={`Switch to ${locale.toUpperCase()}`}
              type="button"
            >
              {locale.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
